import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import { logger } from '../../utils/logger';

export interface Notification {
  _id: string;
  userId: string;
  type: 'message' | 'interest' | 'shortlist' | 'profile_view' | 'plan_expired' | 'system';
  title: string;
  message: string;
  metadata?: {
    senderId?: string;
    senderName?: string;
    senderPhoto?: string;
    chatId?: string;
    activityId?: string;
    [key: string]: any;
  };
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  isDemo: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  isDemo: false,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
};

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notification/fetchNotifications',
  async ({ page = 1, limit = 20 }: { page?: number; limit?: number; forceRefresh?: boolean } = {}, _thunkAPI) => {
    try {
      const token = localStorage.getItem('token');
      
      // If no token, return empty
      if (!token) {
        return {
          notifications: [],
          pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
          isDemo: false,
        };
      }
      
      const { data } = await axios.get(`${API_URL}/notifications`, {
        params: { page, limit },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 3000,
      });
      
      if (data.success) {
        return {
          ...data.data,
          isDemo: false,
        };
      }
      
      // If API returns no success, return empty
      return {
        notifications: [],
        pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
        isDemo: false,
      };
    } catch (_error: any) {
      // Silently handle error - backend endpoint may not be implemented yet
      // Only log non-404 errors
      if (_error.response?.status !== 404) {
        logger.log('Notification fetch error:', _error.message);
      }
      
      // On error, return empty notifications
      return {
        notifications: [],
        pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
        isDemo: false,
      };
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notification/fetchUnreadCount',
  async (_) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // No token, return 0
        return 0;
      }
      
      const { data } = await axios.get(`${API_URL}/notifications/unread-count`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 3000,
      });
      
      return data?.data?.count || 0;
    } catch (_error: any) {
      // On error (including 404), return 0
      return 0;
    }
  }
);

export const markAsRead = createAsyncThunk(
  'notification/markAsRead',
  async (notificationId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      const { data } = await axios.put(`${API_URL}/notifications/${notificationId}/read`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      return data?.data?.notification || notificationId;
    } catch (_error: any) {
      // On error, still return the notificationId for optimistic update
      return notificationId;
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  'notification/markAllAsRead',
  async (_) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      
      await axios.put(`${API_URL}/notifications/mark-all-read`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      return true;
    } catch (_error: any) {
      return false;
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notification/deleteNotification',
  async (notificationId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      await axios.delete(`${API_URL}/notifications/${notificationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      return notificationId;
    } catch (_error: any) {
      return notificationId;
    }
  }
);

// Slice
const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    // Add a new notification (e.g., from Socket.IO)
    addNotification: (state, action: PayloadAction<Notification>) => {
      logger.log('📥 [NotificationSlice] Adding notification:', action.payload);
      logger.log('📊 [NotificationSlice] Current notifications count:', state.notifications.length);
      logger.log('📊 [NotificationSlice] Current unread count:', state.unreadCount);
      
      // Check if notification already exists
      const exists = state.notifications.find(n => n._id === action.payload._id);
      if (exists) {
        logger.log('⚠️ [NotificationSlice] Notification already exists, skipping');
        return;
      }
      
      state.notifications.unshift(action.payload);
      if (!action.payload.isRead) {
        state.unreadCount += 1;
      }
      
      logger.log('✅ [NotificationSlice] Notification added. New count:', state.notifications.length);
      logger.log('✅ [NotificationSlice] New unread count:', state.unreadCount);
    },
    
    // Mark notification as read locally
    markNotificationRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n._id === action.payload);
      if (notification && !notification.isRead) {
        notification.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    
    // Increment unread count
    incrementUnreadCount: (state) => {
      logger.log('📈 [NotificationSlice] Incrementing unread count from', state.unreadCount, 'to', state.unreadCount + 1);
      state.unreadCount += 1;
    },
    
    // Decrement unread count
    decrementUnreadCount: (state) => {
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    },
    
    // Reset unread count
    resetUnreadCount: (state) => {
      state.unreadCount = 0;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Clear all notifications
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
    
    // Mark notifications as read by sender (for auto-read when opening chat)
    markNotificationsBySenderAsRead: (state, action: PayloadAction<{ senderId: string; type?: string }>) => {
      const { senderId, type } = action.payload;
      logger.log('📖 [NotificationSlice] Marking notifications as read for sender:', senderId, 'type:', type);
      
      let markedCount = 0;
      state.notifications.forEach(n => {
        // Match by senderId and optionally by type
        const matchesSender = n.metadata?.senderId === senderId || n.metadata?.fromUserId === senderId;
        const matchesType = type ? n.type === type : true;
        
        if (matchesSender && matchesType && !n.isRead) {
          n.isRead = true;
          markedCount++;
        }
      });
      
      state.unreadCount = Math.max(0, state.unreadCount - markedCount);
      logger.log('✅ [NotificationSlice] Marked', markedCount, 'notifications as read. New unread count:', state.unreadCount);
    },
  },
  extraReducers: (builder) => {
    // Fetch notifications
    builder.addCase(fetchNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNotifications.fulfilled, (state, action) => {
      state.loading = false;
      
      logger.log('📦 [NotificationSlice] Fetch fulfilled:', action.payload);
      logger.log('📊 [NotificationSlice] Current state before merge:', {
        currentCount: state.notifications.length,
        currentUnread: state.unreadCount,
        fetchedCount: action.payload.notifications.length,
        isDemo: action.payload.isDemo
      });
      
      // Separate socket notifications (they have real-time IDs) from demo notifications
      const socketNotifications = state.notifications.filter(n => !n._id.startsWith('demo-'));
      logger.log('🔌 [NotificationSlice] Socket notifications:', socketNotifications.length);
      
      // If this is demo data
      if (action.payload.isDemo) {
        // Keep socket notifications at the top, then add demo notifications
        const existingIds = new Set(socketNotifications.map(n => n._id));
        const newDemoNotifications = action.payload.notifications.filter(
          (n: Notification) => !existingIds.has(n._id)
        );
        state.notifications = [...socketNotifications, ...newDemoNotifications];
        logger.log('✅ [NotificationSlice] Merged with demo data. Total:', state.notifications.length);
      } else {
        // Real API data - merge with socket notifications
        const apiNotifications = action.payload.notifications;
        const existingIds = new Set(apiNotifications.map((n: Notification) => n._id));
        const newSocketNotifications = socketNotifications.filter(
          n => !existingIds.has(n._id)
        );
        state.notifications = [...newSocketNotifications, ...apiNotifications];
        logger.log('✅ [NotificationSlice] Merged with API data. Total:', state.notifications.length);
      }
      
      state.pagination = action.payload.pagination;
      state.isDemo = action.payload.isDemo || false;
      state.error = null;
      
      // Recalculate unread count from actual notifications
      const unreadFromNotifications = state.notifications.filter(n => !n.isRead).length;
      logger.log('📊 [NotificationSlice] Recalculated unread:', unreadFromNotifications);
      // Use the count from notifications to ensure consistency
      state.unreadCount = unreadFromNotifications;
    });
    builder.addCase(fetchNotifications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch unread count
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload;
    });

    // Mark as read
    builder.addCase(markAsRead.fulfilled, (state, action) => {
      const notification = state.notifications.find(n => n._id === action.payload);
      if (notification && !notification.isRead) {
        notification.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    });

    // Mark all as read
    builder.addCase(markAllAsRead.fulfilled, (state) => {
      state.notifications.forEach(n => {
        n.isRead = true;
      });
      state.unreadCount = 0;
    });

    // Delete notification
    builder.addCase(deleteNotification.fulfilled, (state, action) => {
      const notification = state.notifications.find(n => n._id === action.payload);
      if (notification && !notification.isRead) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      state.notifications = state.notifications.filter(n => n._id !== action.payload);
    });
  },
});

export const {
  addNotification,
  markNotificationRead,
  incrementUnreadCount,
  decrementUnreadCount,
  resetUnreadCount,
  clearError,
  clearNotifications,
  markNotificationsBySenderAsRead,
} = notificationSlice.actions;

export default notificationSlice.reducer;